import {
  faArrowUpRightFromSquare,
  faEnvelope,
  faFileInvoice,
  faMessage
} from '@fortawesome/pro-light-svg-icons'
import {
  faCalendarDays,
  faCrosshairsSimple,
  faListCheck,
  faStickyNote
} from '@fortawesome/pro-solid-svg-icons'
import type { ReactElement } from 'react'
import { useContext, useState } from 'react'
import { ActionsItem } from '~/components/Global/Interactions/ActionsItem/ActionsItem'
import { ActionsMenu } from '~/components/Global/Interactions/ActionsMenu/ActionsMenu'
import { ActionsMenuItem } from '~/components/Global/Interactions/ActionsMenu/ActionsMenuItem'
import { EmailAction } from '~/components/Scopes/Actions/EmailAction'
import { MeetingAction } from '~/components/Scopes/Actions/MeetingAction'
import { MessageAction } from '~/components/Scopes/Actions/MessageAction'
import { NoteAction } from '~/components/Scopes/Actions/NoteAction'
import { CreateOrderModal } from '~/domains/orders/components/CreateOrderModal'
import { TaskAction } from '~/domains/tasks/components/TaskAction/TaskAction'
import { AsideActionContext } from '~/utils/contextes'

type ContactRowActionsProps = {
  contactId: number
  companyId: number
  contactUrl: string
  email: string
  phoneMobile?: string | null
  acceptsMessaging: boolean
  validPhoneMobile?: boolean | null
}

const smsDisabledReason = ({
  phoneMobile,
  acceptsMessaging,
  validPhoneMobile
}: Pick<
  ContactRowActionsProps,
  'phoneMobile' | 'acceptsMessaging' | 'validPhoneMobile'
>): string | undefined => {
  if (!acceptsMessaging) {
    return 'Le contact ne souhaite pas être contacté par SMS'
  }
  if (!phoneMobile || phoneMobile.trim() === '') {
    return "Le contact n'a pas de numéro de téléphone mobile renseigné"
  }
  if (validPhoneMobile === false) {
    return 'Le numéro de téléphone mobile est invalide'
  }
  return undefined
}

export const ContactRowActions = ({
  contactId,
  companyId,
  contactUrl,
  email,
  phoneMobile,
  acceptsMessaging,
  validPhoneMobile
}: ContactRowActionsProps): ReactElement => {
  const { setAsideAction } = useContext(AsideActionContext)
  const [openCreateModal, setOpenCreateModal] = useState<
    'order' | 'recourse' | null
  >(null)

  const smsTooltip = smsDisabledReason({
    phoneMobile,
    acceptsMessaging,
    validPhoneMobile
  })
  const missingEmail = email.trim() === ''

  return (
    <>
      <ActionsItem
        title="Voir la fiche (nouvel onglet)"
        icon={faArrowUpRightFromSquare}
        onClick={(e) => {
          e.stopPropagation()
          window.open(contactUrl, '_blank')?.focus()
        }}
      />
      <ActionsItem
        title="Envoyer un SMS"
        icon={faMessage}
        disabled={smsTooltip !== undefined}
        tooltip={smsTooltip}
        onClick={(e) => {
          e.stopPropagation()
          if (smsTooltip !== undefined) return
          setAsideAction({
            action: <MessageAction contactIds={[contactId]} />
          })
        }}
      />
      <ActionsItem
        title="Envoyer un email"
        icon={faEnvelope}
        disabled={missingEmail}
        tooltip={
          missingEmail
            ? "Le contact n'a pas d'adresse email renseignée"
            : undefined
        }
        onClick={(e) => {
          e.stopPropagation()
          if (missingEmail) return
          setAsideAction({
            action: <EmailAction contactsIds={[contactId]} sendTo="contact" />,
            isWide: true
          })
        }}
      />
      <ActionsMenu>
        {[
          <ActionsMenuItem
            key="task"
            icon={faListCheck}
            title="Ajouter une tâche"
            onClick={(e) => {
              e.stopPropagation()
              setAsideAction({
                action: <TaskAction contactIds={[contactId]} />
              })
            }}
          />,
          <ActionsMenuItem
            key="meeting"
            icon={faCalendarDays}
            title="Planifier un rendez-vous"
            onClick={(e) => {
              e.stopPropagation()
              setAsideAction({
                action: <MeetingAction contactIds={[contactId]} />
              })
            }}
          />,
          <ActionsMenuItem
            key="note"
            icon={faStickyNote}
            title="Créer une note"
            onClick={(e) => {
              e.stopPropagation()
              setAsideAction({
                action: <NoteAction contactIds={[contactId]} />
              })
            }}
          />,
          <ActionsMenuItem
            key="create-recourse"
            icon={faCrosshairsSimple}
            title="Créer un recours"
            onClick={(e) => {
              e.stopPropagation()
              setOpenCreateModal('recourse')
            }}
          />,
          <ActionsMenuItem
            key="create-order"
            icon={faFileInvoice}
            title="Créer une commande"
            onClick={(e) => {
              e.stopPropagation()
              setOpenCreateModal('order')
            }}
          />
        ]}
      </ActionsMenu>

      {openCreateModal !== null && (
        <CreateOrderModal
          companyId={companyId}
          contactIds={[contactId]}
          resourceType={openCreateModal}
          open
          onClose={() => setOpenCreateModal(null)}
        />
      )}
    </>
  )
}
